import React, { Component } from "react";
import axios from "axios";
import moment from "moment";
import Helmet from "react-helmet";
import Layout from "../../../Layout/layout";
import { apiHost } from "../../../api/apiClient";
import "../../../styles/reports/runSheets.scss";

class RunSheetMissingGps extends Component {
  constructor(props) {
    super(props);

    const query = new URLSearchParams(props.location.search);
    this.state = {
      selectedDate: query.get("runDate") || moment().format("YYYY-MM-DD"),
      selectedCustomerId: query.get("customerId") || "",
      scope: query.get("scope") || "all",
      customers: [],
      stops: [],
      summary: null,
      loading: false,
      errorMessage: "",
      integrationNotConfigured: false,
    };
  }

  componentDidMount() {
    this.initialiseCustomerContext();
  }

  getAuthHeaders = () => ({
    Authorization: "Bearer " + localStorage.getItem("token"),
  });

  isSystemAdmin = () => {
    return (localStorage.getItem("role") || "").toString().toLowerCase() === "systemadmin";
  };

  getStoredCustomerId = () => {
    try {
      const customer = JSON.parse(localStorage.getItem("customer"));
      return customer ? customer.customerId || customer.id || customer.CustomerId || "" : "";
    } catch (e) {
      return "";
    }
  };

  getCustomerId = () => {
    return this.state.selectedCustomerId || this.getStoredCustomerId();
  };

  initialiseCustomerContext = async () => {
    if (!this.isSystemAdmin()) {
      this.loadMissingGpsStops();
      return;
    }

    try {
      const response = await axios.get(`${apiHost}/api/Customer/GetAllCustomers?AllRecords=true&SortDirection=asc&SortString=name`, {
        headers: this.getAuthHeaders(),
      });
      const customers = response.data || [];
      const customerId = this.state.selectedCustomerId || this.getStoredCustomerId();

      this.setState(
        {
          customers,
          selectedCustomerId: customers.some((customer) => customer.id === customerId) ? customerId : "",
        },
        this.loadMissingGpsStops
      );
    } catch (e) {
      this.setState({ errorMessage: "Unable to load customers for run sheet GPS review." });
    }
  };

  buildQuery = () => {
    const params = new URLSearchParams();
    if (this.state.scope === "current") {
      params.set("runDate", this.state.selectedDate);
    }

    const customerId = this.getCustomerId();
    if (customerId) {
      params.set("customerId", customerId);
    }

    return params.toString();
  };

  hasGpsCoordinates = (stop) => {
    const latitude = Number(stop.latitude);
    const longitude = Number(stop.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude !== 0 && longitude !== 0;
  };

  compareText = (left, right) => {
    return (left || "").toString().trim().toLowerCase().localeCompare((right || "").toString().trim().toLowerCase(), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  formatAddress = (stop) => {
    return [stop.address1, stop.address2, stop.address3, stop.address4, stop.postcode].filter(Boolean).join(", ");
  };

  normaliseMissingGpsValue = (value) => {
    return (value || "").toString().trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  };

  getNormalisedMissingGpsAddress = (stop) => {
    const normalisedCustomerName = this.normaliseMissingGpsValue(stop.customerName);
    const normalisedCustomerCode = this.normaliseMissingGpsValue(stop.customerCode);
    const addressParts = [stop.address1, stop.address2, stop.address3, stop.address4, stop.postcode]
      .filter(Boolean)
      .reduce((parts, value) => parts.concat(value.toString().split(",")), [])
      .map(this.normaliseMissingGpsValue)
      .filter(Boolean)
      .filter((value) => value !== "vic" && value !== "australia")
      .filter((value) => !(value.length === 4 && /^\d+$/.test(value)))
      .filter((value) => value !== normalisedCustomerName && value !== normalisedCustomerCode);

    const uniqueParts = Array.from(new Set(addressParts));
    const firstAddressPart = uniqueParts.findIndex((value) => /\d/.test(value));

    return uniqueParts.slice(firstAddressPart > 0 ? firstAddressPart : 0).join("");
  };

  getGoogleMapsUrl = (stop) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.formatAddress(stop))}`;
  };

  getMissingGpsKey = (stop) => {
    const reference = this.normaliseMissingGpsValue(stop.customerCode) || this.normaliseMissingGpsValue(stop.customerName);
    return `${reference}|${this.getNormalisedMissingGpsAddress(stop)}`;
  };

  formatDropNumbers = (dropNumbers) => {
    const values = (dropNumbers || [])
      .map((dropNumber) => Number(dropNumber))
      .filter((dropNumber) => Number.isFinite(dropNumber))
      .sort((left, right) => left - right);

    return values.length ? values.join(", ") : "";
  };

  dedupeMissingGpsStops = (stops) => {
    const uniqueStops = {};

    stops.forEach((stop) => {
      const key = this.getMissingGpsKey(stop);
      const dropNumber = Number(stop.dropNumber);

      if (!uniqueStops[key]) {
        uniqueStops[key] = {
          ...stop,
          missingDropNumbers: [],
        };
      }

      if (Number.isFinite(dropNumber) && !uniqueStops[key].missingDropNumbers.includes(dropNumber)) {
        uniqueStops[key].missingDropNumbers.push(dropNumber);
      }
    });

    return Object.values(uniqueStops);
  };

  mergeMissingCoordinateRecords = (records) => {
    const uniqueRecords = {};

    records.forEach((record) => {
      const key = this.getMissingGpsKey(record);
      if (!uniqueRecords[key]) {
        uniqueRecords[key] = { ...record };
        return;
      }

      const existing = uniqueRecords[key];
      const currentCodes = (existing.customerCode || "").split(",").map((code) => code.trim()).filter(Boolean);
      const nextCodes = (record.customerCode || "").split(",").map((code) => code.trim()).filter(Boolean);
      existing.customerCode = Array.from(new Set([...currentCodes, ...nextCodes])).sort().join(", ");
      existing.seenCount = Number(existing.seenCount || 0) + Number(record.seenCount || 0);
      existing.firstRunDate = moment.min(moment(existing.firstRunDate), moment(record.firstRunDate)).toISOString();
      existing.lastRunDate = moment.max(moment(existing.lastRunDate), moment(record.lastRunDate)).toISOString();
      existing.firstSeenAtUtc = moment.min(moment(existing.firstSeenAtUtc), moment(record.firstSeenAtUtc)).toISOString();
      existing.lastSeenAtUtc = moment.max(moment(existing.lastSeenAtUtc), moment(record.lastSeenAtUtc)).toISOString();
    });

    return Object.values(uniqueRecords);
  };

  getRunLabel = (stop) => {
    const driver = stop.driver || "Unassigned";
    const truck = stop.truckNo ? `Truck ${stop.truckNo}` : stop.vehicleRegistration ? `Rego ${stop.vehicleRegistration}` : "No truck assigned";
    return `${driver} / ${truck}`;
  };

  loadMissingGpsStops = async () => {
    if (this.isSystemAdmin() && !this.getCustomerId()) {
      this.setState({ stops: [], summary: null, errorMessage: "", integrationNotConfigured: false });
      return;
    }

    this.setState({ loading: true, errorMessage: "", integrationNotConfigured: false });

    try {
      if (this.state.scope === "all") {
        const response = await axios.get(`${apiHost}/api/RunSheets/missing-coordinates?${this.buildQuery()}`, {
          headers: this.getAuthHeaders(),
        });

        const missingStops = this.mergeMissingCoordinateRecords(response.data || [])
          .sort((left, right) => this.compareText(left.customerName, right.customerName)
            || this.compareText(left.customerCode, right.customerCode));

        this.setState({
          summary: null,
          stops: missingStops,
          loading: false,
        });
        return;
      }

      const summaryResponse = await axios.get(`${apiHost}/api/RunSheets/summary?${this.buildQuery()}`, {
        headers: this.getAuthHeaders(),
      });
      const stopsResponse = await axios.get(`${apiHost}/api/RunSheets/stops?${this.buildQuery()}`, {
        headers: this.getAuthHeaders(),
      });
      const missingStops = this.dedupeMissingGpsStops((stopsResponse.data && stopsResponse.data.stops ? stopsResponse.data.stops : [])
        .filter((stop) => !this.hasGpsCoordinates(stop)))
        .sort((left, right) => this.compareText(left.driver, right.driver)
          || this.compareText(left.truckNo, right.truckNo)
          || Number((left.missingDropNumbers || [left.dropNumber || 0])[0] || 0) - Number((right.missingDropNumbers || [right.dropNumber || 0])[0] || 0)
          || this.compareText(left.customerName, right.customerName));

      this.setState({
        summary: summaryResponse.data,
        stops: missingStops,
        loading: false,
      });
    } catch (e) {
      const status = e && e.response ? e.response.status : null;
      const code = e && e.response && e.response.data ? e.response.data.code : "";

      this.setState({
        loading: false,
        stops: [],
        summary: null,
        integrationNotConfigured: status === 404 && code === "IntegrationNotConfigured",
        errorMessage:
          status === 404 && code === "IntegrationNotConfigured"
            ? ""
            : status === 404
              ? "No run sheet data is available for the selected date."
              : "Unable to load missing GPS stops.",
      });
    }
  };

  handleCustomerChange = (event) => {
    this.setState({ selectedCustomerId: event.target.value }, this.loadMissingGpsStops);
  };

  handleDateChange = (event) => {
    this.setState({ selectedDate: event.target.value }, this.loadMissingGpsStops);
  };

  handleScopeChange = (event) => {
    this.setState({ scope: event.target.value }, this.loadMissingGpsStops);
  };

  backToRunSheets = () => {
    const params = new URLSearchParams();
    params.set("runDate", this.state.selectedDate);
    const customerId = this.getCustomerId();
    if (customerId) {
      params.set("customerId", customerId);
    }
    this.props.history.push(`/Rpt-RunSheets?${params.toString()}`);
  };

  affectedDrivers = () => {
    return new Set(this.state.stops.map((stop) => stop.driver || "Unassigned")).size;
  };

  affectedRuns = () => {
    if (this.state.scope === "all") {
      return new Set(this.state.stops.map((stop) => moment(stop.lastRunDate).format("YYYY-MM-DD"))).size;
    }

    return new Set(this.state.stops.map((stop) => `${stop.driver || "Unassigned"}|${stop.truckNo || stop.vehicleRegistration || ""}`)).size;
  };

  totalSightings = () => {
    return this.state.stops.reduce((total, stop) => total + Number(stop.seenCount || 0), 0);
  };

  renderTable = () => {
    if (this.state.loading) {
      return <div className="runsheetLoading">Loading missing GPS stops...</div>;
    }

    if (this.state.integrationNotConfigured) {
      return <div className="runsheetInfo">Run sheet ERP integration is not enabled for this organisation.</div>;
    }

    if (this.state.errorMessage) {
      return <div className="runsheetError">{this.state.errorMessage}</div>;
    }

    if (!this.state.stops.length) {
      return <div className="runsheetEmpty">No missing GPS coordinates were found.</div>;
    }

    const isAllScope = this.state.scope === "all";

    return (
      <section className="runsheetQualityPanel">
        <div className="runsheetQualityHeader">
          <div>
            <span>Accredo data quality</span>
            <h3>{isAllScope ? "All known missing GPS coordinates" : "Stops missing GPS coordinates"}</h3>
          </div>
          <p>Update these customer address coordinates in Accredo, then re-sync the run sheet so VisionTrak can map them correctly.</p>
        </div>
        <div className="runsheetQualityTableWrap">
          <table className="runsheetQualityTable">
            <thead>
              <tr>
                <th>{isAllScope ? "Last run" : "Run"}</th>
                <th>{isAllScope ? "Seen" : "Drop"}</th>
                <th>Customer</th>
                <th>Address</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {this.state.stops.map((stop) => (
                <tr key={stop.id}>
                  <td>{isAllScope ? moment(stop.lastRunDate).format("DD/MM/YYYY") : this.getRunLabel(stop)}</td>
                  <td>
                    <span className="runsheetDropBadgeStatic">{isAllScope ? stop.seenCount : this.formatDropNumbers(stop.missingDropNumbers) || stop.dropNumber}</span>
                  </td>
                  <td>
                    <strong>{stop.customerName}</strong>
                    <span>{stop.customerCode ? `ERP code: ${stop.customerCode}` : "ERP code unavailable"}</span>
                  </td>
                  <td>
                    <div className="runsheetAddressAction">
                      <span>{this.formatAddress(stop)}</span>
                      <a className="runsheetMapButton" href={this.getGoogleMapsUrl(stop)} target="_blank" rel="noopener noreferrer">
                        Map
                      </a>
                    </div>
                  </td>
                  <td>
                    <span className="runsheetGpsBadge">Missing GPS</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  render() {
    const needsCustomerSelection = this.isSystemAdmin() && !this.getCustomerId();

    return (
      <Layout>
        <Helmet>
          <title>Run Sheet Missing GPS</title>
        </Helmet>
        <div className="runsheetPage">
          <section className="runsheetHero">
            <div className="runsheetToolbar">
              <div>
                <button type="button" className="runsheetBackButton" onClick={this.backToRunSheets}>
                  Back to Run Sheets
                </button>
                <h2>Run Sheet Missing GPS</h2>
                <p>{this.state.scope === "all" ? "Outstanding ERP delivery sites that need coordinates" : this.state.summary ? moment(this.state.summary.runDate).format("dddd Do MMMM, YYYY") : "Delivery stops that need coordinates"}</p>
              </div>
              <div className="runsheetFilters">
                {this.isSystemAdmin() && (
                  <select value={this.state.selectedCustomerId} onChange={this.handleCustomerChange}>
                    <option value="">Select customer</option>
                    {this.state.customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                )}
                <select value={this.state.scope} onChange={this.handleScopeChange}>
                  <option value="all">All missing GPS</option>
                  <option value="current">Current run sheet</option>
                </select>
                {this.state.scope === "current" && <input type="date" value={this.state.selectedDate} onChange={this.handleDateChange} />}
                <button type="button" onClick={this.loadMissingGpsStops} disabled={this.state.loading}>
                  Refresh
                </button>
              </div>
            </div>
            <div className="runsheetMetrics">
              <div className="runsheetMetric runsheetMetricWarning">
                <span>Missing GPS</span>
                <strong>{this.state.stops.length}</strong>
              </div>
              <div className="runsheetMetric">
                <span>{this.state.scope === "all" ? "Sightings" : "Drivers affected"}</span>
                <strong>{this.state.scope === "all" ? this.totalSightings() : this.affectedDrivers()}</strong>
              </div>
              <div className="runsheetMetric">
                <span>{this.state.scope === "all" ? "Run dates affected" : "Runs affected"}</span>
                <strong>{this.affectedRuns()}</strong>
              </div>
            </div>
          </section>

          {needsCustomerSelection ? <div className="runsheetError">Select a customer to review missing GPS stops.</div> : this.renderTable()}
        </div>
      </Layout>
    );
  }
}

export default RunSheetMissingGps;
